import { Action, Alert } from 'corso-admin-ui';
import { CrewMerchantUi, isTruthy } from 'corso-types';
import { formatter } from 'corso-web-lib';
import { ComponentProps } from 'react';
import { match } from 'ts-pattern';
import { useConciergeCustomer } from '~/hooks/useConciergeCustomer';
import { useCurrency } from '~/hooks/useCurrency';
import { useIsCorsoCxAgent } from '~/hooks/useIsCorsoAdmin';
import { useStoreCustomer } from '~/hooks/useStoreCustomer';
import { useStoreId } from '~/providers/StoreUserProvider';
import { useShippingClaim } from '../ShippingClaimReviewProvider';
import { useCanEditShippingClaim } from '../hooks/useCanEditShippingClaim';
import { useShippingClaimSlaAlerts } from '../hooks/useShippingClaimLookupConfig';

type ClaimAlert = Pick<
  ComponentProps<typeof Alert>,
  'heading' | 'dismissible'
> & {
  key: string;
  variant: NonNullable<ComponentProps<typeof Alert>['variant']>;
  message: string;
  action?: ComponentProps<typeof Action>;
};

const variantSeverity = {
  critical: 0,
  warning: 1,
  info: 2,
  tip: 3,
  default: 4,
} as const satisfies Record<NonNullable<ClaimAlert['variant']>, number>;

// TODO: Let's rework this when we have a new alert? Please!!
function buildExistingClaimAlert({
  message,
  to,
  key,
}: {
  message: string;
  to?: string;
  key: string;
}) {
  return {
    key,
    heading: 'Duplicate Claim',
    variant: 'critical',
    message,
    dismissible: true,
    action: to ? { to, children: 'View' } : undefined,
  } as const satisfies ClaimAlert;
}

/** Another claim against the same order is the customer asking twice for the same thing. */
function useExistingOrderClaimAlerts() {
  const shippingClaim = useShippingClaim();
  const storeId = useStoreId();

  const { data: storeCustomer } = useStoreCustomer(
    shippingClaim.customerEmail ?? undefined,
  );

  const orderId = shippingClaim.originalStoreOrder.id;
  const orders = storeCustomer?.orders ?? [];

  const shippingClaimsForOrder = orders
    .flatMap((order) => order.shipProtectClaims)
    .filter(
      (sp) => sp.id !== shippingClaim.id && sp.originalStoreOrderId === orderId,
    );

  const claimsForOrder = orders
    .flatMap((order) => order.claims)
    .filter((claim) => claim.originalStoreOrderId === orderId);

  return [
    ...shippingClaimsForOrder.map((sp) =>
      buildExistingClaimAlert({
        message: `Shipping claim #${sp.id} was already submitted for this order.`,
        to: `/${storeId}/claims/shipping/${sp.id}`,
        key: `shippingClaim-${sp.id}`,
      }),
    ),
    ...claimsForOrder.map((claim) =>
      buildExistingClaimAlert({
        message: `${claim.claimType} #${claim.externalId} was already submitted for this order.`,
        key: `claim-${claim.id}`,
      }),
    ),
  ] satisfies ClaimAlert[];
}

/** Repeat shipping claims on one email, at any merchant, are a fraud signal. */
function useRepeatShippingClaimsAlert() {
  const { customerEmail } = useShippingClaim();
  const { data: customer } = useConciergeCustomer(customerEmail ?? undefined);

  const count = customer?.shippingClaims.length ?? 0;
  if (count < 2) return undefined;

  return {
    key: 'repeat-shipping-claims',
    variant: 'warning',
    heading: 'Repeat Shipping Claims',
    message: `${customerEmail} has filed ${count} shipping claims across all merchants.`,
  } as const satisfies ClaimAlert;
}

/** A refund can only pay out what the platform still has available on the order. */
function useUnrefundableValueAlert() {
  const { resolutionMethod, financialSummary, originalStoreOrder } =
    useShippingClaim();
  const { currencyCode } = useCurrency();
  const { totalValue, refundableAmount } = financialSummary;
  const { platformAdminUrl } = originalStoreOrder;

  if (resolutionMethod !== 'Refund') return undefined;
  // the platform could not be reached, so there is nothing to compare against
  if (refundableAmount === undefined) return undefined;
  // nothing at all left to refund is its own alert
  if (refundableAmount === 0) return undefined;
  if (totalValue <= refundableAmount) return undefined;

  const claimed = formatter.currency(totalValue, currencyCode);
  const available = formatter.currency(refundableAmount, currencyCode);

  return {
    key: 'non-refundable-amount',
    variant: 'warning',
    heading: 'Refund Exceeds Available Amount',
    message: `The total value of the claim is ${claimed}, but only ${available} is still refundable on this order.`,
    action: platformAdminUrl
      ? {
          to: platformAdminUrl,
          target: '_blank',
          children: 'View on Shopify',
          icon: 'externalLink',
        }
      : undefined,
  } as const satisfies ClaimAlert;
}

/** Line items added since filing put the claim above what the customer originally asked for. */
function useGreaterThanOriginalValueAlert() {
  const shippingClaim = useShippingClaim();
  const { currencyCode } = useCurrency();
  const { totalValue } = shippingClaim.financialSummary;
  const createdEvent = shippingClaim.timeline?.find(
    (v) => v.message === CrewMerchantUi.shippingClaimCreatedTimelineEventTitle,
  );

  const createdValue = createdEvent?.meta?.find(
    (meta) => meta.kind === 'money' && meta.label === 'Value',
  );

  if (createdValue?.kind !== 'money') return undefined;
  if (totalValue <= createdValue.amount) return undefined;

  const current = formatter.currency(totalValue, currencyCode);
  const original = formatter.currency(createdValue.amount, currencyCode);

  return {
    key: 'greater-than-original-value',
    variant: 'info',
    heading: 'Claim Exceeds Original Value',
    message: `The total value of the claim is ${current}, but only ${original} was originally claimed.`,
  } as const satisfies ClaimAlert;
}

/** Shopify has nothing left to refund on the order, so a refund cannot pay out anything. */
function useNoRefundableValueAlert() {
  const { resolutionMethod, financialSummary, originalStoreOrder } =
    useShippingClaim();
  const { platformAdminUrl } = originalStoreOrder;

  if (resolutionMethod !== 'Refund') return undefined;
  if (financialSummary.refundableAmount !== 0) return undefined;

  return {
    key: 'no-refundable-amount',
    variant: 'warning',
    heading: 'Nothing Refundable on Shopify',
    message: 'Shopify has no refundable value left on this order.',
    action: platformAdminUrl
      ? {
          to: platformAdminUrl,
          target: '_blank',
          children: 'View on Shopify',
          icon: 'externalLink',
        }
      : undefined,
  } as const satisfies ClaimAlert;
}

/** A chargeback already reimburses the customer, so resolving the claim too would pay them twice. */
function useChargebackAlert() {
  const { resolutionMethod, financialSummary, originalStoreOrder } =
    useShippingClaim();
  const { platformAdminUrl } = originalStoreOrder;

  if (!financialSummary.hasChargeback) return undefined;

  const message = match(resolutionMethod)
    .with(
      'Refund',
      () => 'This order has a chargeback. Refunding the claim may fail.',
    )
    .with(
      'Reorder',
      () =>
        'This order has a chargeback. Sending a reorder could replace items the customer is already being reimbursed for.',
    )
    .exhaustive();

  return {
    key: 'order-chargeback',
    variant: 'warning',
    heading: 'Order Has a Chargeback',
    message,
    action: platformAdminUrl
      ? {
          to: platformAdminUrl,
          target: '_blank',
          children: 'View on Shopify',
          icon: 'externalLink',
        }
      : undefined,
  } as const satisfies ClaimAlert;
}

function useShopifyFinancialAlerts() {
  return [
    useNoRefundableValueAlert(),
    useUnrefundableValueAlert(),
    useChargebackAlert(),
  ];
}

export function ShippingClaimAlerts() {
  const lookupSlaAlerts = useShippingClaimSlaAlerts();
  const isCorsoCxAgent = useIsCorsoCxAgent();
  const canEdit = useCanEditShippingClaim();
  const existingClaimAlerts = useExistingOrderClaimAlerts();
  const shopifyFinancialAlerts = useShopifyFinancialAlerts();
  const greaterThanOriginalValueAlert = useGreaterThanOriginalValueAlert();
  const repeatShippingClaimsAlert = useRepeatShippingClaimsAlert();

  const slaAlerts = lookupSlaAlerts.map(
    ({ variant, title, message }, i) =>
      ({
        key: `slaAlert-${i}`,
        variant: match(variant)
          .with('danger', () => 'critical' as const)
          .otherwise((v) => v),
        heading: title,
        message,
      }) satisfies ClaimAlert,
  );

  const alerts: ClaimAlert[] = [
    // an SLA alert asks the agent to act, which a resolved or invoiced claim cannot
    ...(canEdit
      ? [...slaAlerts, ...shopifyFinancialAlerts, greaterThanOriginalValueAlert]
      : []),
    ...existingClaimAlerts,
    repeatShippingClaimsAlert,
  ]
    .filter(isTruthy)
    // stable, so alerts of equal severity keep the order they were built in
    .sort((a, b) => variantSeverity[a.variant] - variantSeverity[b.variant]);

  // ? only corso CX agents should see these alerts!
  if (!alerts.length || !isCorsoCxAgent) return null;

  return (
    <div className="mb-4 space-y-2">
      {alerts.map(({ key, heading, variant, dismissible, message, action }) => (
        <Alert
          key={key}
          elevated
          heading={heading}
          variant={variant}
          dismissible={dismissible}
        >
          <div className="flex items-center justify-between gap-2">
            <p>{message}</p>
            {action && <Action {...action} />}
          </div>
        </Alert>
      ))}
    </div>
  );
}
